/** @jsx jsx */
import { RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { jsx, css } from '@emotion/core'
import { Box, Grid } from '@material-ui/core'

import { messageSelectors } from 'store/messages'
import { toggleOpenCloseWidget } from 'store/general/actions'
import { triggerMessageOpenedGaLogger } from 'utils/googleAnalytics'

import { ChatOrientation } from 'model/ChatPosition'
import { SSPX } from 'constants/themeConstants'
import { bottomTriggerDistanceMultiplier } from 'constants/widgetSizeConstants'
import { MAIN_FONT_FAMILY } from 'constants/textConstants'
import { chatPosition } from 'constants/apiConstants'
import { appearFromBottomAnimation } from 'styles/animations/animations'
import { smartColors } from 'styles/colors'
import { strongBoxShadow, strongBoxShadowHover } from 'styles/boxShadows'

import { useAgentAvatarStyles } from 'components/shared/AgentAvatar'
import { AvatarSection } from 'components/chatHeader/AvatarSection'
import ChatHeaderTitle from 'components/chatHeader/ChatHeaderTitle'

import TriggerCloseButton from './TriggerCloseButton'

type OwnProps = {
	children: React.ReactNode
	renderCloseButton?: boolean
	reference: RefObject<HTMLDivElement>
}

const WIDGET_TYPING_BUBBLES_WIDTH = 70
const WIDGET_TRIGGER_MESSAGE_WIDTH = 235

const TriggerWrapper = ({ children, reference, renderCloseButton }: OwnProps) => {
	const showTyping = useSelector(messageSelectors.showTriggerTypingAnimation)
	const lastUnreadTrigger = useSelector(messageSelectors.lastUnreadTriggerMessage)

	const dispatch = useDispatch()
	const classes = useAgentAvatarStyles()

	const handleWidgetToggle = () => {
		triggerMessageOpenedGaLogger(lastUnreadTrigger.trigger?.name)
		dispatch(toggleOpenCloseWidget(true))
	}

	return (
		<Box
			css={popupCss}
			position="absolute"
			width={showTyping ? WIDGET_TYPING_BUBBLES_WIDTH : WIDGET_TRIGGER_MESSAGE_WIDTH}
			bottom={bottomTriggerDistanceMultiplier * SSPX}
			right={chatPosition === ChatOrientation.Right ? SSPX : undefined}
			left={chatPosition === ChatOrientation.Left ? SSPX : undefined}
			whiteSpace="pre-wrap"
		>
			<Grid
				container
				css={messageGridCss}
				direction="column"
				onClick={handleWidgetToggle}
				ref={reference}
				data-testid="triggerBubble"
			>
				{!showTyping && (
					<Box css={headerFlexCss} pt={1} px={2}>
						<AvatarSection triggerVersion display="flex" mr={1} mt={0.5} className={classes.small} />
						{lastUnreadTrigger.agentId && <ChatHeaderTitle triggerVersion />}
					</Box>
				)}
				<Grid container alignItems="center" justifyContent="flex-start" direction="column">
					{children}
				</Grid>
			</Grid>
			{renderCloseButton && (
				<Box
					position="absolute"
					mt={1}
					left={chatPosition === ChatOrientation.Right ? 0 : undefined}
					right={chatPosition === ChatOrientation.Left ? 0 : undefined}
				>
					<TriggerCloseButton />
				</Box>
			)}
		</Box>
	)
}

export default TriggerWrapper

const headerFlexCss = css({
	display: 'flex',
	overflow: 'hidden',
	width: '100%',
	boxSizing: 'border-box',
})

const messageGridCss = css({
	borderRadius: '5px',
	fontSize: '14px',
	lineHeight: '20px',
	color: smartColors.inputText,
	fontFamily: MAIN_FONT_FAMILY,
	background: smartColors.white,
	overflow: 'hidden',
	cursor: 'pointer',
	borderTop: `1px solid ${smartColors.borderGrey}`,
	borderBottom: `1px solid ${smartColors.white}`,
	transition: '0.3s all',
	boxShadow: strongBoxShadow,
	'&:hover': {
		transition: '0.3s all',
		boxShadow: strongBoxShadowHover,
	},
})

const popupCss = css({
	animation: `${appearFromBottomAnimation} 175ms ease`,
	transition: 'all 0.1s',
})
